import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { CalendarDays, Users } from 'lucide-react';

/**
 * Wide-screen dashboard pass (2026-09): "today's schedule ... visible
 * without scrolling" — a compact read of today's crew assignments, day-
 * granularity (the `schedule` table has no time-of-day field).
 */
export const TodayScheduleCard = ({ entries = [] }) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] h-full"
      data-testid="today-schedule-card"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {t('dashboard.today_schedule.eyebrow', { defaultValue: 'Today' })}
            </div>
            <h2 className="mt-0.5 text-base sm:text-lg font-semibold text-foreground">
              {t('dashboard.today_schedule.title', { defaultValue: "Today's Schedule" })}
            </h2>
          </div>
          <CalendarDays size={16} className="text-[hsl(var(--primary))]/70" />
        </div>

        {entries.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t('dashboard.today_schedule.empty', { defaultValue: 'Nothing on the books today.' })}
          </div>
        ) : (
          <ul className="divide-y divide-border/60" data-testid="today-schedule-list">
            {entries.map((e) => (
              <li key={e.id} className="py-2.5" data-testid={`today-schedule-item-${e.id}`}>
                <div className="text-sm font-semibold text-foreground truncate">{e.jobName}</div>
                <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                  {e.crew.length > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <Users size={11} />
                      {e.crew.join(', ')}
                    </span>
                  ) : null}
                  {e.note ? <span className="truncate">{e.note}</span> : null}
                </div>
              </li>
            ))}
          </ul>
        )}

        <Link
          to="/dashboard/schedule"
          className="mt-3 inline-block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:underline"
          data-testid="today-schedule-view-all"
        >
          {t('dashboard.today_schedule.view_all', { defaultValue: 'View full schedule' })}
        </Link>
      </CardContent>
    </Card>
  );
};

export default TodayScheduleCard;
